import React from 'react';
import moment from 'moment';

const CouponCard = ({ code, width = 600, height = 650, templateUrl = '/images/coupon-ticket.png' }) => {
	const getUsageAmountLabel = (codeObj) => {
		const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);
		if (isNum(codeObj?.maxClaimsByUser)) return `${codeObj.maxClaimsByUser}x`;
		if (isNum(codeObj?.maxClaims)) return `${codeObj.maxClaims}x`;
		const p = codeObj?.promotion;
		if (!p) return '';
		if (isNum(p.percentage)) return `${p.percentage}%`;
		if (isNum(p.amount)) return String(p.amount);
		if (isNum(p.value)) return String(p.value);
		if (isNum(p.discount)) return String(p.discount);
		return '';
	};

	const formatDateLabel = (value) => {
		if (!value) return '';
		const knownFormats = ['DD-MM-YYYY HH:mm', 'DD.MM.YYYY, HH:mm', 'DD.MM.YYYY HH:mm', moment.ISO_8601];
		let m;
		if (value instanceof Date) {
			m = moment(value);
		} else {
			m = moment(value, knownFormats, true);
			if (!m.isValid()) m = moment(value);
		}
		return m.isValid() ? m.format('DD-MM-YYYY HH:mm') : '';
	};

	const startLabel = formatDateLabel(code.startTime);
	const endLabel = formatDateLabel(code.endTime);
	const amountLabel = getUsageAmountLabel(code);
	const promoType = code.promotion?.type ?? '';

	const px = (xFrac, yFrac) => ({ left: xFrac * width, top: yFrac * height });

	const namePos = px(0.5, 0.12); // centered code text
	const codeLabelPos = px(0.5, 0.07); // label above code with extra spacing
	// Logo centered under description
	const logoCenterPos = px(0.5, 0.6);
	const promoTypePos = px(0.18, 0.32); // left upper
	const amountPos = px(0.82, 0.32); // right upper
	const descriptionPos = px(0.5, 0.46); // centered middle
	// Valid block (labels + pills)
	const validTitlePos = px(0.5, 0.72); // centered title "Valid"
	const fromLabelPos = px(0.27, 0.76); // above left pill label "from"
	const untilLabelPos = px(0.73, 0.76); // above right pill label "until"
	const startTimePos = px(0.27, 0.82); // pill left bottom
	const endTimePos = px(0.73, 0.82); // pill right bottom

	// Palette tuned to better match the ticket artwork (no orange)
	const PRIMARY = '#0e7490'; // cyan-600 for main texts
	const ACCENT = '#065f46'; // emerald-800 for labels/pills

	const titleClass = 'font-semibold';
	const fieldClass = '';
	const centerClass = 'absolute text-center';
	const leftClass = 'absolute';
	const rightClass = 'absolute';

	if (!code) return null;

	const scale = width / 600;
	const fs = (px) => `${Math.round(px * scale)}px`;

	const pillClass = 'inline-flex align-items-center justify-content-center border-1 border-round-md';

	return (
		<div
			id={`coupon-${code.id}`}
			className="relative border-round-2xl overflow-hidden"
			style={{
				width: `${width}px`,
				height: `${height}px`,
				backgroundImage: `url(${templateUrl})`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
			}}
			aria-label={`Coupon ${code.name}`}
		>
			<div
				className="absolute"
				style={{
					top: `${Math.round(height * 0.205)}px`,
					left: `${Math.round(width * 0.08)}px`,
					width: `${Math.round(width * 0.84)}px`,
					borderTop: '2px dashed rgba(0,0,0,0.24)',
				}}
			/>
			<div
				className="absolute"
				style={{
					top: `${Math.round(height * 0.665)}px`,
					left: `${Math.round(width * 0.08)}px`,
					width: `${Math.round(width * 0.84)}px`,
					borderTop: '2px dashed rgba(0,0,0,0.18)',
				}}
			/>

			<img
				src="/branding/alberts-logo.png"
				alt="Alberts logo"
				className="absolute"
				style={{
					top: `${logoCenterPos.top - 13}px`,
					left: `${logoCenterPos.left}px`,
					transform: 'translateX(-50%)',
					width: `${Math.round(width * 0.34)}px`,
					opacity: 0.1,
					filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.05))',
				}}
			/>

			<div
				className={centerClass}
				style={{
					top: `${codeLabelPos.top}px`,
					left: `${codeLabelPos.left}px`,
					transform: 'translateX(-50%)',
					fontSize: fs(16),
					color: ACCENT,
					fontWeight: 600,
					letterSpacing: '0.06em',
					textTransform: 'uppercase',
					fontFamily: 'Poppins, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
					backgroundColor: 'rgba(6,95,70,0.08)',
					border: '1px solid rgba(6,95,70,0.25)',
					borderRadius: `${Math.max(10 * scale, 8)}px`,
					padding: `${Math.max(4 * scale, 3)}px ${Math.max(8 * scale, 6)}px`,
				}}
			>
				Use code
			</div>
			<div
				className={`${centerClass} ${titleClass}`}
				style={{
					top: `${namePos.top + 12}px`,
					left: `${namePos.left}px`,
					transform: 'translateX(-50%)',
					fontSize: fs(26),
					color: PRIMARY,
					letterSpacing: '0.08em',
					textShadow: '0 1px 0 rgba(0,0,0,0.05)',
					fontFamily: 'Poppins, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
				}}
			>
				{(code.name || '').toString().toUpperCase()}
			</div>

			<div
				className={`${leftClass} ${fieldClass}`}
				style={{
					top: `${promoTypePos.top}px`,
					left: `${promoTypePos.left}px`,
					fontSize: fs(26),
					fontWeight: 600,
					color: PRIMARY,
					letterSpacing: '0.01em',
					textShadow: '0 1px 0 rgba(0,0,0,0.04)',
					fontFamily: 'Poppins, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
				}}
			>
				<span
					style={{
						backgroundColor: 'rgba(14,116,144,0.10)',
						border: '1px solid rgba(14,116,144,0.30)',
						borderRadius: `${Math.max(12 * scale, 10)}px`,
						padding: `${Math.max(6 * scale, 5)}px ${Math.max(12 * scale, 10)}px`,
					}}
				>
					{promoType}
				</span>
			</div>

			<div
				className={`${rightClass} ${fieldClass}`}
				style={{
					top: `${amountPos.top}px`,
					left: `${amountPos.left}px`,
					transform: 'translateX(-100%)',
					fontSize: fs(26),
					fontWeight: 600,
					color: PRIMARY,
					letterSpacing: '0.01em',
					textShadow: '0 1px 0 rgba(0,0,0,0.04)',
					fontFamily: 'Poppins, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
				}}
			>
				<span
					style={{
						backgroundColor: 'rgba(14,116,144,0.10)',
						border: '1px solid rgba(14,116,144,0.30)',
						borderRadius: `${Math.max(12 * scale, 10)}px`,
						padding: `${Math.max(6 * scale, 5)}px ${Math.max(12 * scale, 10)}px`,
					}}
				>
					{amountLabel}
				</span>
			</div>

			{code.description ? (
				<div
					className={`${centerClass} ${fieldClass}`}
					style={{
						top: `${descriptionPos.top}px`,
						left: `${descriptionPos.left}px`,
						transform: 'translateX(-50%)',
						maxWidth: `${width * 0.8}px`,
						fontSize: fs(20),
						color: PRIMARY,
						textShadow: '0 1px 0 rgba(0,0,0,0.03)',
					}}
				>
					<span
						style={{
							backgroundColor: 'rgba(14,116,144,0.08)',
							border: '1px solid rgba(14,116,144,0.25)',
							borderRadius: `${Math.max(12 * scale, 10)}px`,
							padding: `${Math.max(8 * scale, 6)}px ${Math.max(12 * scale, 10)}px`,
							display: 'inline-block',
							textAlign: 'center',
						}}
					>
						{code.description}
					</span>
				</div>
			) : null}

			{startLabel || endLabel ? (
				<>
					<div
						className={`${centerClass} ${fieldClass}`}
						style={{
							top: `${validTitlePos.top}px`,
							left: `${validTitlePos.left}px`,
							transform: 'translateX(-50%)',
							fontSize: fs(22),
							color: ACCENT,
							fontWeight: 600,
						}}
					>
						Valid
					</div>

					{startLabel ? (
						<div
							className={`${centerClass} ${fieldClass}`}
							style={{
								top: `${fromLabelPos.top}px`,
								left: `${fromLabelPos.left}px`,
								transform: 'translateX(-50%)',
								fontSize: fs(18),
								color: ACCENT,
								fontWeight: 500,
							}}
						>
							from
						</div>
					) : null}

					{endLabel ? (
						<div
							className={`${centerClass} ${fieldClass}`}
							style={{
								top: `${untilLabelPos.top}px`,
								left: `${untilLabelPos.left}px`,
								transform: 'translateX(-50%)',
								fontSize: fs(18),
								color: ACCENT,
								fontWeight: 500,
							}}
						>
							until
						</div>
					) : null}
				</>
			) : null}

			{startLabel ? (
				<div
					className="absolute"
					style={{
						top: `${startTimePos.top}px`,
						left: `${startTimePos.left}px`,
						transform: 'translateX(-50%)',
					}}
				>
					<span
						className={pillClass}
						style={{
							border: '1px solid rgba(14,116,144,0.25)',
							color: ACCENT,
							backgroundColor: 'rgba(14,116,144,0.08)',
							fontSize: fs(18),
							padding: `${Math.max(8 * scale, 6)}px ${Math.max(22 * scale, 16)}px`,
							maxWidth: `${Math.round(width * 0.26)}px`,
							width: '100%',
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							textAlign: 'center',
							lineHeight: 1.2,
							whiteSpace: 'normal',
							wordBreak: 'break-word',
						}}
					>
						{startLabel}
					</span>
				</div>
			) : null}

			{endLabel ? (
				<div
					className="absolute"
					style={{
						top: `${endTimePos.top}px`,
						left: `${endTimePos.left}px`,
						transform: 'translateX(-50%)',
					}}
				>
					<span
						className={pillClass}
						style={{
							border: '1px solid rgba(14,116,144,0.25)',
							color: ACCENT,
							backgroundColor: 'rgba(14,116,144,0.08)',
							fontSize: fs(18),
							padding: `${Math.max(8 * scale, 6)}px ${Math.max(22 * scale, 16)}px`,
							maxWidth: `${Math.round(width * 0.26)}px`,
							width: '100%',
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							textAlign: 'center',
							lineHeight: 1.2,
							whiteSpace: 'normal',
							wordBreak: 'break-word',
						}}
					>
						{endLabel}
					</span>
				</div>
			) : null}
		</div>
	);
};

export default CouponCard;
